import type { JsonValue, JsonObject, JsonArray } from 'type-fest';
import { validate as isUuid } from 'uuid';

import { isObject } from './isObject';

export const toCamelCase = (str: string) => {
  if (str.includes(' ')) return str;
  return str.replace(/^([A-Z])|[\s-_](\w)/g, function (_, p1, p2) {
    if (p2) return p2.toUpperCase();
    return p1.toLowerCase();
  });
};

export const keysToCamelCase = (obj: JsonObject): JsonObject =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      isUuid(key) ? key : toCamelCase(key),
      value,
    ]),
  );

export function keysToCamelCaseDeep(obj: JsonValue): JsonValue {
  if (Object(obj) !== obj) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map((thing) => keysToCamelCaseDeep(thing)) as JsonArray;
  }
  return Object.keys(obj as JsonObject).reduce(
    (acc: JsonObject, key: string) => {
      if (
        key === 'overrides' ||
        key === 'metadata' ||
        key === 'configuration_parameters'
      ) {
        acc[key] = (obj as JsonObject)[key];
      } else {
        const sub = (obj as JsonObject)[key];
        const newKey =
          key.includes(' ') ||
          isAllUpperCase(key) ||
          ['-', '_'].every((char) => !key.includes(char))
            ? key
            : key
                .split(',')
                .map((key) => (isUuid(key) ? key : toCamelCase(key)))
                .join();
        if (isObject(sub)) {
          acc[newKey] = keysToCamelCaseDeep(sub);
        } else {
          acc[newKey] = (obj as JsonObject)[key];
        }
      }
      return acc;
    },
    {} as JsonObject,
  );
}

export const toSnakeCase = (str: string) => {
  if (str.includes(' ')) return str;
  return str.replace(/^([A-Z])|([A-Z])|[\s-](\w)/g, function (_, p1, p2, p3) {
    if (p2) return '_' + p2.toLowerCase();
    if (p3) return '_' + p3.toLowerCase();
    return p1.toLowerCase();
  });
};

export const keysToSnakeCase = (obj: JsonObject): JsonObject =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      isUuid(key) ? key : toSnakeCase(key),
      value,
    ]),
  );

export function keysToSnakeCaseDeep(obj: JsonValue): JsonValue {
  // base case -- primitives
  if (Object(obj) !== obj) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((element) => keysToSnakeCaseDeep(element));
  }

  return Object.keys(obj as JsonObject).reduce(
    (acc: JsonObject, key: string) => {
      if (
        key === 'overrides' ||
        key === 'metadata' ||
        key === 'configuration_parameters'
      ) {
        acc[key] = (obj as JsonObject)[key];
      } else {
        const sub = (obj as JsonObject)[key];
        const newKey =
          key.includes(' ') || isAllSameCase(key)
            ? key
            : key
                .split(',')
                .map((key) => (isUuid(key) ? key : toSnakeCase(key)))
                .join();

        if (isObject(sub)) {
          acc[newKey] = keysToSnakeCaseDeep(sub);
        } else {
          acc[newKey] = (obj as JsonObject)[key];
        }
      }
      return acc;
    },
    {},
  );
}

export function isAllUpperCase(str: string): boolean {
  return str === str.toUpperCase();
}

export function isAllLowerCase(str: string): boolean {
  return str === str.toLowerCase();
}

export function isAllSameCase(str: string): boolean {
  return isAllUpperCase(str) || isAllLowerCase(str);
}

/**
 * Take a first name and last name combo and return a single string in the format
 * '<first_name> <last_initial>.'
 * @param firstName - first name string
 * @param lastName - last name string
 */
export const formatFirstNameLastInitial = (
  firstName: string,
  lastName: string,
): string => `${firstName} ${lastName?.charAt(0)}.`;

/**
 * Take a first name and last name combo and return a single string in the format
 * '<first_initial><last_initial>'
 * @param firstName - first name string
 * @param lastName - last name string
 */
export const formatInitials = (firstName: string, lastName: string): string =>
  `${firstName?.charAt(0)}${lastName?.charAt(0)}`;
